import { useEffect } from 'react';

/**
 * Lightweight SEO hook that updates document meta tags at runtime.
 * SPA approach — good for users, OK for Google (uses Headless Chrome).
 * For perfect SEO we would need SSR, but this covers 90% of cases.
 */
const SITE_NAME = 'AeroDeals';
const DEFAULT_OG_IMAGE = 'https://www.aerodeals.it/logo-header.png';

function upsertMeta(name, content, isProperty = false) {
  if (!content) return;
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(id, data) {
  if (!data) return;
  let el = document.querySelector(`script[data-seo-id="${id}"]`);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute('data-seo-id', id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/**
 * @param {Object} opts
 * @param {string} opts.title - Page title (without site name — added automatically)
 * @param {string} opts.description - Meta description
 * @param {string} [opts.image] - OG image URL
 * @param {string} [opts.canonical] - Canonical URL (defaults to current location)
 * @param {string} [opts.type] - og:type (website | article | product)
 * @param {Object} [opts.jsonLd] - Structured data JSON-LD object
 * @param {string} [opts.keywords] - Meta keywords (comma-separated)
 */
export function useSEO({ title, description, image, canonical, type = 'website', jsonLd, keywords } = {}) {
  useEffect(() => {
    const fullTitle = title ? (title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`) : `${SITE_NAME} — Marketplace Aeronautico Italiano`;
    document.title = fullTitle;

    const canonicalUrl = canonical || (typeof window !== 'undefined' ? window.location.href.split('?')[0] : '');
    const ogImage = image || DEFAULT_OG_IMAGE;

    upsertMeta('description', description);
    if (keywords) upsertMeta('keywords', keywords);

    // Open Graph
    upsertMeta('og:title', fullTitle, true);
    upsertMeta('og:description', description, true);
    upsertMeta('og:type', type, true);
    upsertMeta('og:image', ogImage, true);
    upsertMeta('og:site_name', SITE_NAME, true);
    upsertMeta('og:url', canonicalUrl, true);
    upsertMeta('og:locale', 'it_IT', true);

    // Twitter
    upsertMeta('twitter:card', 'summary_large_image');
    upsertMeta('twitter:title', fullTitle);
    upsertMeta('twitter:description', description);
    upsertMeta('twitter:image', ogImage);

    // Canonical
    upsertLink('canonical', canonicalUrl);

    // JSON-LD structured data
    if (jsonLd) {
      upsertJsonLd('page', jsonLd);
    } else {
      const el = document.querySelector('script[data-seo-id="page"]');
      if (el) el.remove();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, image, canonical, type, keywords, JSON.stringify(jsonLd || {})]);
}

export default useSEO;
